<script setup lang="ts">
/**
 * Public pricing. Every number on this page comes from GET /api/public/plans
 * (config/plans.php on the backend) — the same source PlanLimits enforces
 * and the tenant billing page charges against. Never hardcode a price or a
 * limit here; a mismatch between what we promise and what the code enforces
 * is exactly the bug this replaced.
 */
definePageMeta({ layout: 'marketing' })

const { selfServePlans, fetchPlans, formatLimit } = usePlans()
await fetchPlans()

const cheapest = computed(() => {
  const prices = selfServePlans.value.map(p => p.price_aud ?? Infinity)
  return prices.length ? Math.min(...prices) : null
})

useSeoMeta({
  title: () => `Caseflow pricing — from AUD $${cheapest.value ?? ''}/month`,
  description: 'Per-agency pricing for migration agents, education agents, law firms and real estate agencies. Three plans, 14-day free trial, cancel anytime.',
  ogTitle: () => `Caseflow pricing — from AUD $${cheapest.value ?? ''}/month`,
  ogDescription: 'Three plans, 14-day free trial, cancel anytime.',
  ogImage: '/og-image.png',
  twitterCard: 'summary_large_image',
  twitterImage: '/og-image.png'
})

useStructuredData({
  '@type': 'Product',
  'name': 'Caseflow',
  'description': 'Case management for migration, education, legal and real estate agencies',
  'brand': { '@type': 'Brand', 'name': 'Caseflow' },
  'offers': selfServePlans.value.map(p => ({
    '@type': 'Offer',
    'name': p.name,
    'priceCurrency': 'AUD',
    'price': String(p.price_aud),
    'availability': 'https://schema.org/InStock',
    'priceValidUntil': '2027-12-31',
    'url': '/signup'
  }))
})

const accentFor = (key: string) => ({
  starter: 'from-gray-500 to-gray-700',
  growth: 'from-indigo-500 to-purple-500',
  scale: 'from-teal-500 to-emerald-500'
} as Record<string, string>)[key] ?? 'from-gray-500 to-gray-700'

const analytics = useAnalytics()
onMounted(() => analytics.track('Pricing Viewed'))

const onSignupClick = (plan: string) =>
  analytics.track('Signup Clicked', { source: 'pricing', plan })

const faq = [
  { q: 'Can I switch between plans?', a: 'Yes, any time, from Settings → Billing. Upgrades are pro-rated by Stripe; downgrades take effect at the end of your current billing cycle.' },
  { q: 'Is there a free trial?', a: 'Yes — 14 days, no credit card required. Import your client list, run real cases, and only add a card when you decide to keep going.' },
  { q: 'What happens to my data if I cancel?', a: 'Your workspace stays readable until the end of the paid period, and we keep your data for 30 days after that in case you change your mind. Ask us for a full export at any time.' },
  { q: 'Do I pay per user or per agency?', a: 'Per agency. Each plan includes a number of staff seats; move up a plan when you need more.' },
  { q: 'What about transaction fees?', a: 'When your clients pay you by card, Stripe charges its standard processing fees. We charge nothing on top of those.' },
  { q: 'Can I bring my own Xero account?', a: 'Yes — every agency connects their own Xero organisation. We push invoices as drafts; you review and authorise inside Xero.' },
  { q: 'Which industries is it built for?', a: 'Migration agents, education agents, law firms and real estate agencies (sales and leasing). Each gets its own workflows, fields, key dates and intake form on day one.' },
  { q: 'What about Enterprise?', a: 'For multi-office groups or agencies with more than 15 users, we offer custom Enterprise pricing with dedicated infrastructure and SLAs.' }
]
</script>

<template>
  <div>
    <section class="mx-auto max-w-7xl px-6 pt-20 pb-12 text-center">
      <p class="text-xs uppercase tracking-wider font-medium text-indigo-600">
        Pricing
      </p>
      <h1 class="mt-3 text-4xl md:text-5xl font-bold tracking-tight text-gray-900">
        Pricing built for small agencies.
      </h1>
      <p class="mt-4 text-gray-600 max-w-2xl mx-auto">
        Three plans, all in AUD, billed monthly. 14-day free trial, no card needed. No setup fees.
      </p>
    </section>

    <section class="mx-auto max-w-7xl px-6 pb-20">
      <div
        v-if="!selfServePlans.length"
        class="text-center text-sm text-gray-500 py-12"
      >
        Pricing is temporarily unavailable. <NuxtLink
          to="/contact"
          class="text-indigo-600 hover:underline"
        >Contact us</NuxtLink> for a quote.
      </div>

      <div
        v-else
        class="grid md:grid-cols-3 gap-6"
      >
        <div
          v-for="p in selfServePlans"
          :key="p.key"
          :class="[
            'rounded-2xl border bg-white p-7 flex flex-col',
            p.highlight ? 'border-2 border-indigo-500 shadow-xl scale-[1.02]' : 'border-gray-200'
          ]"
        >
          <div
            v-if="p.highlight"
            class="mb-3"
          >
            <span class="inline-block text-[10px] uppercase tracking-wider bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-2 py-0.5 rounded-full font-medium">Most popular</span>
          </div>
          <p class="text-sm font-medium text-gray-500">
            {{ p.name }}
          </p>
          <p class="mt-2 text-4xl font-bold text-gray-900">
            AUD ${{ p.price_aud }}<span class="text-base font-normal text-gray-500">/mo</span>
          </p>
          <p class="mt-2 text-sm text-gray-600">
            {{ p.tagline }}
          </p>

          <dl class="mt-5 grid grid-cols-3 gap-2 text-center text-xs">
            <div class="rounded-lg bg-gray-50 py-2">
              <dt class="text-gray-500">
                Users
              </dt>
              <dd class="font-semibold text-gray-900">
                {{ formatLimit(p.limits.staff_seats) }}
              </dd>
            </div>
            <div class="rounded-lg bg-gray-50 py-2">
              <dt class="text-gray-500">
                Active cases
              </dt>
              <dd class="font-semibold text-gray-900">
                {{ formatLimit(p.limits.active_cases) }}
              </dd>
            </div>
            <div class="rounded-lg bg-gray-50 py-2">
              <dt class="text-gray-500">
                Storage
              </dt>
              <dd class="font-semibold text-gray-900">
                {{ p.limits.storage_gb === null ? 'Unlimited' : `${p.limits.storage_gb} GB` }}
              </dd>
            </div>
          </dl>

          <ul class="mt-6 space-y-2.5 text-sm text-gray-700 flex-1">
            <li
              v-for="f in (p.highlights ?? []).slice(3)"
              :key="f"
              class="flex gap-2"
            >
              <span class="text-emerald-600">✓</span>{{ f }}
            </li>
          </ul>
          <NuxtLink
            :to="`/signup?plan=${p.key}`"
            :class="[
              'mt-7 block text-center rounded-lg px-5 py-3 text-sm font-medium',
              p.highlight
                ? `bg-gradient-to-br ${accentFor(p.key)} text-white hover:opacity-95`
                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
            ]"
            @click="onSignupClick(p.key)"
          >Start free trial</NuxtLink>
        </div>
      </div>

      <p class="mt-8 text-center text-sm text-gray-600">
        Need Enterprise? <NuxtLink
          to="/contact"
          class="text-indigo-600 hover:underline"
        >Talk to us</NuxtLink> about custom contracts, dedicated SLAs, and more than 15 users.
      </p>
    </section>

    <section class="mx-auto max-w-3xl px-6 pb-24">
      <h2 class="text-3xl font-bold tracking-tight text-gray-900 text-center">
        Pricing FAQ
      </h2>
      <div class="mt-8 divide-y divide-gray-100 border-y border-gray-100">
        <details
          v-for="(item, i) in faq"
          :key="i"
          class="group py-5"
        >
          <summary class="cursor-pointer flex justify-between items-center text-base font-medium text-gray-900 list-none">
            {{ item.q }}
            <span class="ml-4 text-gray-400 group-open:rotate-180 transition">▾</span>
          </summary>
          <p class="mt-3 text-sm text-gray-600 leading-relaxed">
            {{ item.a }}
          </p>
        </details>
      </div>
    </section>
  </div>
</template>
